/**
 * Create the store with dynamic reducers
 */
import Raven from 'raven-js';
import createRavenMiddleware from 'raven-for-redux';
import { createStore, applyMiddleware, compose } from 'redux';
import { routerMiddleware } from 'connected-react-router';
import createSagaMiddleware from 'redux-saga';
import { createTracker } from 'redux-segment';
import { stateTransformer } from './utils/stateTransformer';
import createReducer from './reducers';
import {
  previousRouterPathnameMiddleware,
  preventSegmentForGuestUsersMiddleware,
} from './customMiddleware';

const sagaMiddleware = createSagaMiddleware();
const segmentTracker = createTracker();

if (SENTRY_DSN && SENTRY_ENVIRONMENT !== 'development') {
  Raven.config(SENTRY_DSN, {
    environment: SENTRY_ENVIRONMENT,
  }).install();
}

export default function configureStore(initialState = {}, history) {
  // Create the store with two middlewares
  // 1. sagaMiddleware: Makes redux-sagas work
  // 2. routerMiddleware: Syncs the location/URL path to the state

  let middlewares = [];

  if (process.env.NODE_ENV === 'development') {
    /* eslint-disable global-require */
    const reduxImmutableStateInvariant = require('redux-immutable-state-invariant');
    const { logger } = require(`redux-logger`);

    middlewares = [
      ...middlewares,
      reduxImmutableStateInvariant.default(),
      logger,
    ];
  }

  if (process.env.NODE_ENV === 'production') {
    middlewares = [
      ...middlewares,
      createRavenMiddleware(Raven, { stateTransformer }),
      preventSegmentForGuestUsersMiddleware(segmentTracker),
    ];
  }

  middlewares = [...middlewares, sagaMiddleware, routerMiddleware(history)];

  const enhancers = [
    applyMiddleware(previousRouterPathnameMiddleware, ...middlewares),
  ];

  // If Redux DevTools Extension is installed use it, otherwise use Redux compose
  /* eslint-disable no-underscore-dangle */
  const composeEnhancers =
    process.env.NODE_ENV !== 'production' &&
    typeof window === 'object' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
      : compose;
  /* eslint-enable */

  const store = createStore(
    createReducer(),
    initialState,
    composeEnhancers(...enhancers),
  );

  // Extensions
  store.runSaga = sagaMiddleware.run;
  store.injectedReducers = {}; // Reducer registry
  store.injectedSagas = {}; // Saga registry

  // Make reducers hot reloadable, see http://mxs.is/googmo
  /* istanbul ignore next */
  if (module.hot) {
    module.hot.accept('./reducers', () => {
      store.replaceReducer(createReducer(store.injectedReducers));
    });
  }

  return store;
}
